import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { revealField } from '@/lib/passengers/api';
import { revealedName } from '@/lib/pax/pax-display-name';
import { cn } from '@/lib/cn';

// Audited PII reveal for a passenger name (TDS §14): masked by default, revealed
// on demand via the BFF (which logs who/when; the value is never logged). Reused
// by the hero (lead pax) and the per-pax rows so the reveal control is consistent.

export function RevealableName({
  caseUrn,
  paxUrn,
  masked,
  className,
  iconClassName,
}: {
  caseUrn: string;
  /** The passenger URN to reveal; when absent the name shows masked with no control. */
  paxUrn?: string | undefined;
  /** The masked name to show until revealed. */
  masked: string;
  className?: string;
  iconClassName?: string;
}) {
  const { t } = useTranslation('pnr');
  const [revealed, setRevealed] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: () => revealField(caseUrn, paxUrn as string, 'name'),
    onSuccess: (r) => setRevealed(r.value),
  });

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="truncate">{revealedName(revealed) ?? masked}</span>
      {paxUrn ? (
        revealed ? (
          <button
            type="button"
            onClick={() => setRevealed(null)}
            title={t('reveal.hide')}
            className={cn('shrink-0 text-muted-foreground hover:text-foreground', iconClassName)}
            data-testid="pax-hide-name"
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => reveal.mutate()}
            disabled={reveal.isPending}
            title={t('reveal.show')}
            className={cn(
              'shrink-0 text-muted-foreground hover:text-primary disabled:opacity-50',
              iconClassName,
            )}
            data-testid="pax-reveal-name"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
          </button>
        )
      ) : null}
    </span>
  );
}
